import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, DollarSign, Shield, Receipt, Wrench, Fuel,
  FileText, ClipboardList, Users, CreditCard, Settings, LogOut, ChevronRight, Moon, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { signOut } from '../lib/supabase';
import { forgetPushDevice } from '../utils/push';
import { DeleteAccountSheet } from '../components/common/DeleteAccountSheet';
import { useOwner } from '../hooks/useOwner';
import { useTheme } from '../hooks/useTheme';
import { useDisplayName } from '../hooks/useDisplayName';

import { BrandLogo } from '../components/common/BrandLogo';
// Phone "Menu" tab: everything that is not on the bottom bar, under the same names as the
// web menu, grouped like a phone settings screen.
const ALL = ['owner', 'admin', 'editor', 'viewer'];
const GROUPS = [
  {
    title: 'Insights',
    items: [
      { to: '/reports', icon: BarChart3, label: 'Reports', roles: ALL },
    ],
  },
  {
    title: 'Records',
    items: [
      { to: '/expenses', icon: DollarSign, label: 'Expenses', roles: ALL },
      { to: '/insurance', icon: Shield, label: 'Insurance', roles: ALL },
      { to: '/road-tax', icon: Receipt, label: 'Road Tax', roles: ALL },
      { to: '/services', icon: Wrench, label: 'Services', roles: ALL },
      { to: '/mechanical', icon: Fuel, label: 'Mechanical', roles: ALL },
      { to: '/documents', icon: FileText, label: 'Documents', roles: ALL },
      { to: '/forms', icon: ClipboardList, label: 'Forms & Checklists', roles: ALL },
    ],
  },
  {
    title: 'Account',
    items: [
      { to: '/team', icon: Users, label: 'Team', roles: ['owner', 'admin'] },
      { to: '/billing', icon: CreditCard, label: 'Billing', roles: ['owner'] },
      { to: '/settings', icon: Settings, label: 'Settings', roles: ['owner'] },
    ],
  },
];

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', editor: 'Editor', viewer: 'Viewer' };

export const MobileMenu = () => {
  const navigate = useNavigate();
  const { role } = useOwner();
  const { theme, toggleTheme } = useTheme() || {};
  const { fullName, email } = useDisplayName();
  const [showDelete, setShowDelete] = useState(false);

  const handleSignOut = async () => {
    try { await forgetPushDevice(); await signOut(); navigate('/login'); } catch (e) { toast.error('Could not sign out'); }
  };

  return (
    <div className="m-menu">
      <div className="m-profile">
        <div className="m-avatar">{(fullName || email || '?').charAt(0).toUpperCase()}</div>
        <div className="m-profile-text">
          <div className="m-profile-name">{fullName || 'Your account'}</div>
          <div className="m-profile-sub">{email}</div>
        </div>
        {role && <span className="m-role">{ROLE_LABEL[role] || role}</span>}
      </div>

      {GROUPS.map(g => {
        const items = g.items.filter(i => !role || i.roles.includes(role));
        if (items.length === 0) return null;
        return (
          <section key={g.title} className="m-group">
            <div className="m-group-title">{g.title}</div>
            <div className="m-list">
              {items.map(i => (
                <button key={i.to} className="m-row" onClick={() => navigate(i.to)}>
                  <span className="m-row-icon"><i.icon size={18} /></span>
                  <span className="m-row-label">{i.label}</span>
                  <ChevronRight size={18} className="m-row-chev" />
                </button>
              ))}
            </div>
          </section>
        );
      })}

      <section className="m-group">
        <div className="m-group-title">App</div>
        <div className="m-list">
          {toggleTheme && (
            <button className="m-row" onClick={toggleTheme}>
              <span className="m-row-icon"><Moon size={18} /></span>
              <span className="m-row-label">Dark mode</span>
              <span className="m-row-value">{theme === 'dark' ? 'On' : 'Off'}</span>
            </button>
          )}
          <button className="m-row m-row-danger" onClick={handleSignOut}>
            <span className="m-row-icon"><LogOut size={18} /></span>
            <span className="m-row-label">Sign out</span>
          </button>
          <button className="m-row m-row-danger" onClick={() => setShowDelete(true)}>
            <span className="m-row-icon"><Trash2 size={18} /></span>
            <span className="m-row-label">Delete my account</span>
          </button>
        </div>
      </section>

      <div className="m-footer"><div className="m-footer-logo"><BrandLogo width={150} /></div></div>
      {showDelete && <DeleteAccountSheet isOwner={role === 'owner'} onClose={() => setShowDelete(false)} />}
    </div>
  );
};
